import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { medicamentoService, pacienteService, liberacaoService, notificacaoService } from '../services';
import NotificationModal from '../components/NotificationModal';
import { 
  Package, 
  AlertTriangle, 
  TrendingUp, 
  Users, 
  Plus,
  Eye,
  Activity,
  Clock
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalMedicamentos: 0,
    medicamentosAtivos: 0,
    estoqueBaixo: 0,
    medicamentosVencendo: 0,
    medicamentosVencidos: 0,
    totalPacientes: 0,
    liberacoesHoje: 0
  });
  const [medicamentosEstoqueBaixo, setMedicamentosEstoqueBaixo] = useState([]);
  const [medicamentosVencidos, setMedicamentosVencidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [hasCheckedNotifications, setHasCheckedNotifications] = useState(false);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      
      // Calcular datas
      const hoje = new Date();
      const proximoMes = new Date();
      proximoMes.setMonth(hoje.getMonth() + 1);
      const hojeStr = hoje.toISOString().split('T')[0];
      
      // Carregar todos os itens de estoque (com dados dos medicamentos registrados)
      const responseMedicamentos = await medicamentoService.getAll({ limit: 1000 });
      const itensEstoque = responseMedicamentos.medicamentos || [];
      
      // Carregar medicamentos com estoque baixo
      let arrayEstoqueBaixo = [];
      try {
        const estoqueBaixo = await medicamentoService.getEstoqueBaixo();
        arrayEstoqueBaixo = Array.isArray(estoqueBaixo) ? estoqueBaixo : [];
      } catch (error) {
        console.log('Erro ao carregar estoque baixo, definindo como array vazio:', error.message);
        arrayEstoqueBaixo = [];
      }
      setMedicamentosEstoqueBaixo(arrayEstoqueBaixo.slice(0, 5)); // Mostrar apenas os 5 primeiros
      
      // Carregar pacientes
      let totalPacientes = 0;
      try {
        const responsePacientes = await pacienteService.getAll({ limit: 1000 });
        totalPacientes = responsePacientes.total || 0;
      } catch (error) {
        console.log('Erro ao carregar pacientes, definindo como 0:', error.message);
        totalPacientes = 0;
      }
      
      // Liberações de hoje
      let liberacoesHoje = 0;
      try {
        const responseLiberacoesHoje = await liberacaoService.getAll({ 
          dataInicio: hojeStr, 
          dataFim: hojeStr,
          status: 'confirmada'
        });
        liberacoesHoje = responseLiberacoesHoje.total || 0;
      } catch (error) {
        console.log('Erro ao carregar liberações, definindo como 0:', error.message);
        liberacoesHoje = 0;
      }
      
      // Filtrar itens vencendo (próximos 30 dias)
      const medicamentosVencendo = itensEstoque.filter(item => {
        const dataValidade = new Date(item.dataValidade);
        return dataValidade <= proximoMes && dataValidade >= hoje;
      });
      
      // Filtrar itens vencidos
      const medicamentosVencidos = itensEstoque.filter(item => {
        const dataValidade = new Date(item.dataValidade);
        return dataValidade < hoje;
      });

      // Salvar medicamentos vencidos para exibir na tabela
      setMedicamentosVencidos(medicamentosVencidos.slice(0, 5));

      // Calcular estatísticas
      const itensAtivos = itensEstoque.filter(item => 
        item.ativo && (item.medicamentoRegistrado?.ativo ?? true)
      ).length;
      
      // Contar medicamentos únicos registrados
      const medicamentosUnicos = new Set();
      itensEstoque.forEach(item => {
        if (item.medicamentoRegistrado) {
          medicamentosUnicos.add(item.medicamentoRegistrado.id);
        }
      });
      
      setStats({
        totalMedicamentos: medicamentosUnicos.size, // Medicamentos únicos registrados
        medicamentosAtivos: itensAtivos, // Itens de estoque ativos
        estoqueBaixo: arrayEstoqueBaixo.length,
        medicamentosVencendo: medicamentosVencendo.length,
        medicamentosVencidos: medicamentosVencidos.length,
        totalPacientes: totalPacientes,
        liberacoesHoje: liberacoesHoje
      });
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Verificar notificações não lidas após carregamento inicial
  useEffect(() => {
    const verificarNotificacoes = async () => {
      // Só verificar se o usuário não for visitante e se ainda não verificou
      if (user && user.cargo !== 'visitante' && !loading && !hasCheckedNotifications) {
        try {
          // Verificar se já mostrou notificações nesta sessão
          const jaVisto = sessionStorage.getItem('notificationsModalShown');
          
          const countNaoLidas = await notificacaoService.getCountNaoLidas();
          
          setHasCheckedNotifications(true);
          
          // Se há notificações não lidas e não mostrou ainda nesta sessão, abrir o modal automaticamente
          if (countNaoLidas > 0 && !jaVisto) {
            setTimeout(() => {
              setShowNotificationModal(true);
              sessionStorage.setItem('notificationsModalShown', 'true');
            }, 1000); // Delay de 1 segundo para garantir que a UI esteja pronta
          }
        } catch (error) {
          console.error('Erro ao verificar notificações não lidas:', error);
          setHasCheckedNotifications(true);
        }
      }
    };

    verificarNotificacoes();
  }, [user, loading, hasCheckedNotifications]);

  const handleCloseNotificationModal = () => {
    setShowNotificationModal(false);
    // Não limpar o flag do sessionStorage para evitar que apareça novamente na mesma sessão
    // sessionStorage.removeItem('notificationsModalShown');
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: '#333', marginBottom: '10px' }}>
          Bem-vindo, {user?.nome}!
        </h1>
        <p style={{ color: '#667', fontSize: '16px' }}>
          Painel de controle do sistema de farmácia
        </p>
      </div>

      {/* Cards de estatísticas principais */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* Card: Total de Medicamentos */}
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                {stats.totalMedicamentos}
              </h3>
              <p style={{ margin: '0', opacity: 0.9, fontSize: '16px' }}>
                Medicamentos Cadastrados
              </p>
              <p style={{ margin: '5px 0 0 0', opacity: 0.7, fontSize: '14px' }}>
                {stats.medicamentosAtivos} itens em estoque
              </p>
            </div>
            <Package size={52} style={{ opacity: 0.8 }} />
          </div>
        </div>

        {/* Card: Estoque Baixo */}
        <div className="card" style={{ 
          background: stats.estoqueBaixo > 0 
            ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)' 
            : 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
          color: 'white',
          boxShadow: stats.estoqueBaixo > 0 
            ? '0 8px 32px rgba(255, 107, 107, 0.3)' 
            : '0 8px 32px rgba(0, 184, 148, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                {stats.estoqueBaixo}
              </h3>
              <p style={{ margin: '0', opacity: 0.9, fontSize: '16px' }}>
                Itens com Estoque Baixo
              </p>
              <p style={{ margin: '5px 0 0 0', opacity: 0.7, fontSize: '14px' }}>
                {stats.estoqueBaixo > 0 ? 'Reposição necessária' : 'Tudo OK'}
              </p>
            </div>
            <AlertTriangle size={52} style={{ opacity: 0.8 }} />
          </div>
        </div>

        {/* Card: Medicamentos Vencendo */}
        <div className="card" style={{ 
          background: stats.medicamentosVencendo > 0 
            ? 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)' 
            : 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
          color: 'white',
          boxShadow: stats.medicamentosVencendo > 0 
            ? '0 8px 32px rgba(253, 203, 110, 0.3)' 
            : '0 8px 32px rgba(116, 185, 255, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                {stats.medicamentosVencendo}
              </h3>
              <p style={{ margin: '0', opacity: 0.9, fontSize: '16px' }}>
                Itens Vencendo (30 dias)
              </p>
              <p style={{ margin: '5px 0 0 0', opacity: 0.7, fontSize: '14px' }}>
                {stats.medicamentosVencendo > 0 ? 'Verificar validades' : 'Tudo OK'}
              </p>
            </div>
            <Clock size={52} style={{ opacity: 0.8 }} />
          </div>
        </div>

        {/* Card: Medicamentos Vencidos */}
        <div className="card" style={{ 
          background: stats.medicamentosVencidos > 0 
            ? 'linear-gradient(135deg, #d63031 0%, #a4161a 100%)' 
            : 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
          color: 'white',
          boxShadow: stats.medicamentosVencidos > 0 
            ? '0 8px 32px rgba(214, 48, 49, 0.3)' 
            : '0 8px 32px rgba(0, 184, 148, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                {stats.medicamentosVencidos}
              </h3>
              <p style={{ margin: '0', opacity: 0.9, fontSize: '16px' }}>
                Itens Vencidos
              </p>
              <p style={{ margin: '5px 0 0 0', opacity: 0.7, fontSize: '14px' }}>
                {stats.medicamentosVencidos > 0 ? 'Remover do estoque' : 'Tudo OK'}
              </p>
            </div>
            <AlertTriangle size={52} style={{ opacity: 0.8 }} />
          </div>
        </div>

        {/* Card: Pacientes */}
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)',
          color: 'white',
          boxShadow: '0 8px 32px rgba(253, 121, 168, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                {stats.totalPacientes}
              </h3>
              <p style={{ margin: '0', opacity: 0.9, fontSize: '16px' }}>
                Pacientes Cadastrados
              </p>
              <p style={{ margin: '5px 0 0 0', opacity: 0.7, fontSize: '14px' }}>
                Base de clientes
              </p>
            </div>
            <Users size={52} style={{ opacity: 0.8 }} />
          </div>
        </div>

        {/* Card: Liberações Hoje */}
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #55efc4 0%, #00b894 100%)',
          color: 'white',
          boxShadow: '0 8px 32px rgba(85, 239, 196, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                {stats.liberacoesHoje}
              </h3>
              <p style={{ margin: '0', opacity: 0.9, fontSize: '16px' }}>
                Liberações Hoje
              </p>
              <p style={{ margin: '5px 0 0 0', opacity: 0.7, fontSize: '14px' }}>
                {new Date().toLocaleDateString('pt-BR')}
              </p>
            </div>
            <Activity size={52} style={{ opacity: 0.8 }} />
          </div>
        </div>



      </div>

      {/* Ações rápidas */}
      <div className="card">
        <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TrendingUp size={24} />
          Ações Rápidas
        </h3>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <Link to="/medicamentos/novo" className="btn btn-primary">
            <Plus size={20} />
            Novo Medicamento
          </Link>
          <Link to="/pacientes/novo" className="btn btn-primary">
            <Plus size={20} />
            Novo Paciente
          </Link>
          <Link to="/liberacoes/nova" className="btn btn-primary">
            <Plus size={20} />
            Nova Liberação
          </Link>
          <Link to="/medicamentos" className="btn btn-secondary">
            <Eye size={20} />
            Ver Medicamentos
          </Link>
          <Link to="/pacientes" className="btn btn-secondary">
            <Eye size={20} />
            Ver Pacientes
          </Link>
          <Link to="/liberacoes" className="btn btn-secondary">
            <Eye size={20} />
            Ver Liberações
          </Link>
        </div>
      </div>

      {/* Alertas de estoque baixo */}
      {medicamentosEstoqueBaixo.length > 0 && (
        <div className="card">
          <h3 style={{ 
            marginBottom: '20px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            color: '#dc3545'
          }}>
            <AlertTriangle size={24} />
            Itens com Estoque Baixo
          </h3>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Estoque Atual</th>
                  <th>Estoque Mínimo</th>
                  <th>Fabricante</th>
                </tr>
              </thead>
              <tbody>
                {medicamentosEstoqueBaixo.map((item) => {
                  const medicamento = item.medicamentoRegistrado || item;
                  return (
                    <tr key={item.id}>
                      <td style={{ fontWeight: '500' }}>{medicamento.nome}</td>
                      <td>
                        <span style={{ 
                          color: item.estoque === 0 ? '#dc3545' : '#f57c00',
                          fontWeight: 'bold'
                        }}>
                          {item.estoque}
                        </span>
                      </td>
                      <td>{item.estoqueMinimo}</td>
                      <td>{item.fabricante}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {medicamentosEstoqueBaixo.length === 5 && (
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <Link to="/medicamentos" className="btn btn-secondary">
                Ver Todos os Alertas
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Alertas de medicamentos vencidos */}
      {medicamentosVencidos.length > 0 && (
        <div className="card">
          <h3 style={{ 
            marginBottom: '20px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            color: '#dc3545'
          }}>
            <Clock size={24} />
            Itens Vencidos no Estoque
          </h3>
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            <AlertTriangle size={20} />
            <strong>Atenção:</strong> Estes itens estão vencidos e NÃO podem ser dispensados. Remova-os do estoque imediatamente.
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Data de Validade</th>
                  <th>Estoque</th>
                  <th>Fabricante</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {medicamentosVencidos.map((item) => {
                  const medicamento = item.medicamentoRegistrado || item;
                  const diasVencidos = Math.floor((new Date() - new Date(item.dataValidade)) / (1000 * 60 * 60 * 24));
                  return (
                    <tr key={item.id} style={{ backgroundColor: '#fff5f5' }}>
                      <td style={{ fontWeight: '500' }}>{medicamento.nome}</td>
                      <td>
                        <span style={{ 
                          color: '#dc3545',
                          fontWeight: 'bold'
                        }}>
                          {new Date(item.dataValidade).toLocaleDateString('pt-BR')}
                        </span>
                      </td>
                      <td>
                        <span style={{ 
                          color: '#dc3545',
                          fontWeight: 'bold'
                        }}>
                          {item.estoque} unidades
                        </span>
                      </td>
                      <td>{item.fabricante}</td>
                      <td>
                        <span style={{
                          background: '#dc3545',
                          color: 'white',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <AlertTriangle size={12} />
                          Vencido há {diasVencidos} dias
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {medicamentosVencidos.length === 5 && (
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <Link 
                to="/medicamentos?status=vencidos" 
                className="btn btn-danger"
              >
                Ver Todos os Itens Vencidos
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Modal de Notificações */}
      <NotificationModal 
        isOpen={showNotificationModal}
        onClose={handleCloseNotificationModal}
      />
    </div>
  );
};

export default Dashboard;